import { useEffect, useRef, useState, type FormEvent } from 'react';
import { describeCreateProjectError } from '../../data/writers/create-project';

/**
 * The New Project sheet (design export S04).
 *
 * Geometry and copy come from the export: 22px top sheet radius, the grab
 * handle, "New Project", the PROJECT NAME label, the `e.g. Bayfront Tower`
 * placeholder, the STATUS block, the full-width primary button and the Cancel
 * text control beneath it.
 *
 * TWO DELIBERATE DEPARTURES, both disclosed in the handoff:
 *
 * 1. The export's STATUS block is an interactive three-way picker. S-02 creates
 *    Projects as `ongoing` only — choosing FINISHED or CLOSED at creation is
 *    not in this slice, and status changes are S-03. The block is therefore
 *    rendered as a truthful, non-interactive statement of what will happen,
 *    not as a control that looks pickable and is not.
 *
 * 2. The export labels the button "Create & open". Opening the Project means
 *    Project Home, which S-02 explicitly does not build. A button that promises
 *    to open something it cannot open is exactly the fake functionality the
 *    standing rule forbids, so it reads "Create Project".
 */

interface Props {
  onCancel: () => void;
  /** Resolves when the server has confirmed the Project. Rejects on failure. */
  onCreate: (name: string) => Promise<void>;
}

export function CreateProjectSheet({ onCancel, onCreate }: Props) {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // The sheet is a deliberate action, so focus goes to the one field in it.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    // First line of duplicate-submit defence: the guard runs before any await,
    // so a second submit dispatched in the same tick as the first cannot get
    // past it. The disabled button is the visible half; this is the real half.
    if (busy) return;

    const trimmed = name.trim();
    if (!trimmed) {
      setError('Give the Project a name before creating it.');
      inputRef.current?.focus();
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await onCreate(trimmed);
      // The parent closes the sheet only after the server confirms, so there is
      // nothing to reset here — this component unmounts.
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : String(caught);
      setError(describeCreateProjectError(message));
      setBusy(false);
    }
  }

  return (
    <div
      className="sheet-scrim"
      role="dialog"
      aria-modal="true"
      aria-label="New Project"
      onClick={(event) => {
        // Tapping the dimmed area cancels, but never mid-save: closing while a
        // create is in flight would leave Brandon unsure whether it happened.
        if (event.target === event.currentTarget && !busy) onCancel();
      }}
    >
      <form className="sheet" onSubmit={(event) => void handleSubmit(event)} noValidate>
        <div className="sheet-handle" aria-hidden="true" />
        <h2 className="sheet-title">New Project</h2>

        <label className="sheet-field">
          <span className="sheet-label">Project name</span>
          <input
            ref={inputRef}
            className="sheet-input"
            type="text"
            name="projectName"
            placeholder="e.g. Bayfront Tower"
            autoComplete="off"
            maxLength={200}
            value={name}
            onChange={(event) => setName(event.target.value)}
            disabled={busy}
          />
        </label>

        <div className="sheet-label sheet-status-label">Status</div>
        {/* Stated, not chosen. See the note at the top of this file. */}
        <p className="sheet-status" data-testid="create-status">
          <span className="status-chip status-chip--ongoing">● ONGOING</span>
          <span className="sheet-status-note">
            New Projects always start as Ongoing. You can change this later.
          </span>
        </p>

        {error ? (
          <p className="cl-error" role="alert">
            {error}
          </p>
        ) : null}

        <button className="cl-button sheet-submit" type="submit" disabled={busy}>
          {busy ? 'Creating…' : 'Create Project'}
        </button>

        <button className="sheet-cancel" type="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </form>
    </div>
  );
}
